import { useContext, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router";
import classNames from "classnames";
import Button from "../button";
import Checkbox from "../checkbox";
import DeviceSlider from "../device-slider";
import SBPSvg from "../icons/sbp";
import InfoSvg from "../icons/info";
import styles from "./style.module.css";
import {
  DAY_BY_SUBSCRIPTION_TYPE,
  DEVICE_LIMITS,
  DeviceLimit,
  IUserServer,
  PaymentMethod,
  SUBSCRIPTION_TYPE
} from "../../context/types";
import { getTariffPrice } from "../../common/tariffs";
import { createDeviceUpgradeOrder } from "../../api/order";
import { setPendingDeviceLimit as setPendingDeviceLimitApi } from "../../api/user-server";
import { openExternalUrl } from "../../utils/telegram";
import { getApiErrorMessage } from "../../utils/api-error";
import { pluralizeDevices } from "../../utils/pluralizeDevices";
import { DataContext } from "../../context/dataProvider";

const MIN_UPGRADE_AMOUNT = 100;
const MS_PER_DAY = 24 * 60 * 60 * 1000;

interface AddDeviceModalProps {
  open: boolean;
  onClose: () => void;
  userServer: IUserServer;
}

type PreviewState =
  | { state: 'no-change' }
  | { state: 'trial' }
  /** amount уже округлён до минимальной суммы платежа, isMinAmount — был ли расчёт ниже минимума. */
  | { state: 'ready'; amount: number; isMinAmount: boolean }
  /** Активная подписка: уменьшение — в pending до конца периода. */
  | { state: 'decrease-active'; endDate: Date }
  /**
   * Истёкшая подписка: любое изменение только в pendingDeviceLimit
   * (без доплаты — применится при следующей оплате).
   */
  | { state: 'pending-expired'; isDecrease: boolean };

type Step = 'select' | 'confirm-decrease' | 'pay';

function AddDeviceModal({ open, onClose, userServer }: AddDeviceModalProps) {
  const navigate = useNavigate();
  const dataContext = useContext(DataContext);
  const billedDeviceLimit = (userServer.deviceLimit ?? 3) as DeviceLimit;
  const pending = userServer.pendingDeviceLimit ?? null;
  const isExpiredSubscription =
    new Date(userServer.subscriptionEndDate) <= new Date();
  // На активной подписке pending бывает только уменьшением. pending > billed —
  // устаревшее значение (например купили 3, а в pending осталось 5): игнорируем,
  // иначе слайдер встаёт на 5 и апгрейд 3→5 выглядит как «без изменений».
  const queuedPending =
    pending != null && (isExpiredSubscription || pending < billedDeviceLimit)
      ? pending
      : null;
  const displayDeviceLimit = (queuedPending ?? billedDeviceLimit) as DeviceLimit;

  const [deviceLimit, setDeviceLimit] = useState<DeviceLimit>(displayDeviceLimit);
  const [step, setStep] = useState<Step>('select');
  const [payMethod, setPayMethod] = useState<PaymentMethod | null>('sbp');
  const [visible, setVisible] = useState(open);
  const [animated, setAnimated] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setDeviceLimit(displayDeviceLimit);
      setStep('select');
      setPayMethod('sbp');
      setError(null);
      setSubmitting(false);
      setVisible(true);
      const frame = requestAnimationFrame(() => setAnimated(true));
      return () => cancelAnimationFrame(frame);
    }
    setAnimated(false);
    const timer = setTimeout(() => setVisible(false), 320);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const preview: PreviewState = useMemo(() => {
    if (deviceLimit === displayDeviceLimit) {
      return { state: 'no-change' };
    }

    if (userServer.subscriptionType === SUBSCRIPTION_TYPE.TEST) {
      return { state: 'trial' };
    }

    const now = new Date();
    const endDate = new Date(userServer.subscriptionEndDate);
    const isExpired = endDate <= now;

    // На истёкшей подписке покупки нет — только pendingDeviceLimit в любую сторону.
    if (isExpired) {
      return { state: 'pending-expired', isDecrease: deviceLimit < billedDeviceLimit };
    }

    if (deviceLimit < billedDeviceLimit) {
      return { state: 'decrease-active', endDate };
    }

    // Увеличение на активной — доплата от оплаченного лимита (billed), не от pending.
    const subscriptionType = userServer.subscriptionType as SUBSCRIPTION_TYPE;
    const totalDays = DAY_BY_SUBSCRIPTION_TYPE[subscriptionType];
    const remainingDays = (endDate.getTime() - now.getTime()) / MS_PER_DAY;
    const currentPrice = getTariffPrice(subscriptionType, billedDeviceLimit);
    const targetPrice = getTariffPrice(subscriptionType, deviceLimit);
    const rawAmount = ((targetPrice - currentPrice) * remainingDays) / totalDays;
    const ceilAmount = Math.ceil(rawAmount);
    const isMinAmount = ceilAmount < MIN_UPGRADE_AMOUNT;

    return { state: 'ready', amount: isMinAmount ? MIN_UPGRADE_AMOUNT : ceilAmount, isMinAmount };
  }, [deviceLimit, displayDeviceLimit, billedDeviceLimit, userServer]);

  const isDecrease =
    preview.state === 'decrease-active' ||
    (preview.state === 'pending-expired' && preview.isDecrease);

  if (!visible || typeof document === "undefined") return null;

  const handlePay = async () => {
    if (preview.state !== 'ready' || submitting || !payMethod) return;
    setSubmitting(true);
    setError(null);
    try {
      const response = await createDeviceUpgradeOrder({
        paymentMethod: payMethod,
        deviceLimit
      });
      openExternalUrl(response.url);
      onClose();
      navigate(`/payment/confirmation?status=success&orderId=${response.orderId}&k=upgrade`);
    } catch (e) {
      setError(getApiErrorMessage(e, 'Не удалось создать заказ. Попробуйте ещё раз.'));
      setSubmitting(false);
    }
  };

  const handleSavePending = async () => {
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await setPendingDeviceLimitApi(deviceLimit);
      await dataContext?.updateData();
      onClose();
    } catch (e) {
      setError(getApiErrorMessage(e, 'Не удалось сохранить изменение. Попробуйте ещё раз.'));
      setSubmitting(false);
    }
  };

  const handlePrimaryClick = () => {
    if (isDecrease) {
      setError(null);
      setStep('confirm-decrease');
      return;
    }
    if (preview.state === 'ready') {
      setError(null);
      setStep('pay');
      return;
    }
    if (preview.state === 'pending-expired') {
      void handleSavePending();
    }
  };

  const handleConfirmDecrease = () => {
    void handleSavePending();
  };

  let buttonLabel = 'Выберите количество устройств';
  let hint: string | null = null;
  let primaryDisabled = true;
  let showMinAmountNote = false;

  switch (preview.state) {
    case 'trial':
      hint = 'Изменение лимита устройств недоступно на пробном периоде';
      break;
    case 'ready':
      buttonLabel = `Увеличить до ${deviceLimit} за ${preview.amount} ₽`;
      primaryDisabled = false;
      showMinAmountNote = preview.isMinAmount;
      break;
    case 'decrease-active':
      buttonLabel = `Уменьшить до ${deviceLimit}`;
      primaryDisabled = false;
      break;
    case 'pending-expired':
      buttonLabel = preview.isDecrease
        ? `Уменьшить до ${deviceLimit}`
        : `Сохранить ${deviceLimit}`;
      primaryDisabled = false;
      break;
    default:
      break;
  }

  let confirmText = '';
  if (preview.state === 'decrease-active') {
    confirmText = `${billedDeviceLimit} ${pluralizeDevices(billedDeviceLimit)} останутся доступны до ${preview.endDate.toLocaleDateString('ru-RU')}. Новое количество начнет действовать после оплаты следующего периода подписки.`;
  } else if (preview.state === 'pending-expired') {
    confirmText = 'Текущее количество устройств изменится при оплате подписки';
  }

  const payAmount = preview.state === 'ready' ? preview.amount : 0;

  return createPortal(
    <div className={`${styles.overlay} ${animated ? styles.overlayOpen : ""}`}>
      <div className={styles.backdrop} onClick={onClose} role="presentation" />
      <div className={styles.sheet}>
        <div className={styles.panelInner}>
          {step === 'select' && (
            <>
              <h2 className={styles.title}>Количество устройств</h2>
              <div className={classNames(styles.pill, isDecrease && styles.pillDecrease)}>
                {isDecrease
                  ? `Уменьшится до ${deviceLimit}`
                  : `${deviceLimit} ${pluralizeDevices(deviceLimit)}`}
              </div>

              <div className={styles.sliderWrap}>
                <DeviceSlider
                  options={DEVICE_LIMITS}
                  value={deviceLimit}
                  onChange={(value) => setDeviceLimit(value as DeviceLimit)}
                  showLabels
                />
              </div>

              {hint && <p className={styles.hint}>{hint}</p>}
              {showMinAmountNote && (
                <div className={styles.minAmountNote}>
                  <InfoSvg />
                  <span className={styles.minAmountNoteText}>Минимальная сумма к оплате – {MIN_UPGRADE_AMOUNT} ₽</span>
                </div>
              )}
              {error && <p className={styles.error}>{error}</p>}

              <Button
                label={buttonLabel}
                onClick={handlePrimaryClick}
                disabled={primaryDisabled}
                loading={submitting}
              />
              <Button label="Отмена" variant="secondary" onClick={onClose} />
            </>
          )}

          {step === 'confirm-decrease' && (
            <>
              <h2 className={styles.title}>
                Хотите уменьшить количество устройств до {deviceLimit}?
              </h2>
              <p className={styles.confirmText}>{confirmText}</p>
              {error && <p className={styles.error}>{error}</p>}

              <Button
                label={`Да, уменьшить до ${deviceLimit}`}
                onClick={handleConfirmDecrease}
                loading={submitting}
              />
              <Button label="Отмена" variant="secondary" onClick={() => setStep('select')} />
            </>
          )}

          {step === 'pay' && (
            <>
              <div>
                <h2 className={styles.buyTitle}>Расширение подписки</h2>
                <p className={styles.buySubtitle}>
                  {deviceLimit} {pluralizeDevices(deviceLimit)}
                </p>
              </div>
              <div className={styles.buyPayMethods}>
                <Checkbox
                  defaultChecked={payMethod === 'sbp'}
                  name="upgrade-pay-method"
                  label="СБП"
                  onClick={() => setPayMethod('sbp')}
                  icon={<SBPSvg />}
                />
              </div>
              {error && <p className={styles.error}>{error}</p>}
              <Button
                className={styles.buyBtn}
                label={`Оплатить ${payAmount} ₽`}
                onClick={() => void handlePay()}
                loading={submitting}
                disabled={!payMethod}
              />
              <Button
                label="Выбрать другие условия"
                variant="secondary"
                onClick={() => {
                  setError(null);
                  setStep('select');
                }}
              />
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}

export default AddDeviceModal;
